/**
 * 转化链异常编辑器（华为商店红线规则 · 回传异常监控 · parent_id=转化链异常 专用）。
 *
 * 数据契约：ope_value = {"greaterThan0":"register","equal0":"activate"}；
 * 页面展示：greaterThan0 → 「回传大于0指标」、equal0 → 「回传等于0指标」，
 * 两个下拉（选项为已知回传指标代码），变更即 JSON.stringify 回传。
 */

import { useMemo } from "react";

/** 编辑器 props：value 为 ope_value JSON 串，onChange 回传序列化后的新串。 */
interface ConversionChainEditorProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** 已知回传指标代码（初版数据出现的 4 种；展示为「中文 代码」）。 */
const METRIC_OPTIONS: { code: string; label: string }[] = [
  { code: "register", label: "注册 register" },
  { code: "activate", label: "激活 activate" },
  { code: "credit", label: "授信 credit" },
  { code: "firstpay", label: "首次付费 firstpay" },
];

/** ope_value JSON → {greaterThan0, equal0}；非法 JSON 降级为空串（不报错）。 */
function parseValue(value: string): { greaterThan0: string; equal0: string } {
  try {
    const obj: unknown = JSON.parse(value);
    if (obj !== null && typeof obj === "object") {
      const rec = obj as Record<string, unknown>;
      return {
        greaterThan0: typeof rec.greaterThan0 === "string" ? rec.greaterThan0 : "",
        equal0: typeof rec.equal0 === "string" ? rec.equal0 : "",
      };
    }
  } catch {
    // 非法 JSON：降级为空选，由用户重新选择
  }
  return { greaterThan0: "", equal0: "" };
}

export function ConversionChainEditor({
  value,
  onChange,
  disabled = false,
}: ConversionChainEditorProps) {
  const parsed = useMemo(() => parseValue(value), [value]);

  /** 选项列表：已知指标 + 当前值（防库里有新代码时下拉显示空白）。 */
  const optionsFor = (current: string) => {
    const codes = new Set(METRIC_OPTIONS.map((o) => o.code));
    if (current && !codes.has(current)) {
      return [...METRIC_OPTIONS, { code: current, label: current }];
    }
    return METRIC_OPTIONS;
  };

  function emit(next: { greaterThan0: string; equal0: string }) {
    onChange(JSON.stringify({ greaterThan0: next.greaterThan0, equal0: next.equal0 }));
  }

  const greaterOptions = optionsFor(parsed.greaterThan0);
  const equalOptions = optionsFor(parsed.equal0);

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
      <label className="flex items-center gap-2">
        <span className="whitespace-nowrap text-[12px] text-text-muted">回传大于0指标</span>
        <select
          className="leo-select w-44"
          value={parsed.greaterThan0}
          disabled={disabled}
          onChange={(e) => emit({ ...parsed, greaterThan0: e.target.value })}
        >
          {greaterOptions.map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2">
        <span className="whitespace-nowrap text-[12px] text-text-muted">回传等于0指标</span>
        <select
          className="leo-select w-44"
          value={parsed.equal0}
          disabled={disabled}
          onChange={(e) => emit({ ...parsed, equal0: e.target.value })}
        >
          {equalOptions.map((o) => (
            <option key={o.code} value={o.code}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
